// Glass pill-bar: OBLINOR-brand + konsern-piller (mor/datter) + org.nr-søk med
// treff-dropdown. Søk → naviger til /selskapsinfo/:orgnr. Konsern-noder kommer fra
// useKonsern (avledet av Brreg-roller); klikk på en pill bytter aktivt selskap.
import { useState } from "react";

import { cn } from "@/lib/utils";
import { useCompanySearch } from "../api/hooks";
import { SelskapsVelger } from "./SelskapsVelger";
import type { KonsernNode } from "../api/konsern";

interface TopBarProps {
  /** Navn på aktivt selskap (fra Brreg) — vist som fallback-pille. */
  activeName: string | null;
  /** Aktivt org.nr (for å markere riktig konsern-pill). */
  aktivOrg: string;
  /** Konsern-noder (mor/datter) — vises som piller når de finnes. */
  noder: KonsernNode[];
  /** Kalles når et søketreff eller en konsern-pill velges (orgnr). */
  onSelect: (orgnr: string) => void;
}

export function TopBar({ activeName, aktivOrg, noder, onSelect }: TopBarProps) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const { data: hits = [] } = useCompanySearch(q);

  function choose(orgnr: string) {
    setQ("");
    setOpen(false);
    onSelect(orgnr);
  }

  return (
    <div className="glass bar relative mb-[22px] flex items-center gap-2.5 rounded-full px-3.5 py-2.5">
      <span className="px-2 font-extrabold tracking-[0.04em] text-[var(--val)]">OBLINOR</span>

      {noder.length > 0 ? (
        <SelskapsVelger noder={noder} aktivOrg={aktivOrg} onVelg={onSelect} />
      ) : (
        activeName && (
          <button className="pill on" type="button">
            {activeName}
          </button>
        )
      )}

      <div className="relative ml-auto">
        <input
          className="h-8 w-[180px] rounded-full border border-[rgba(255,217,163,0.4)] bg-white/45 px-3.5 text-[13px] text-[var(--ink)] outline-none placeholder:text-[var(--faint)] focus:border-[var(--sunset)]"
          placeholder="Slå opp org.nr…"
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
        />
        {open && q.trim().length >= 2 && hits.length > 0 && (
          <ul className="glass absolute right-0 top-10 z-10 w-[260px] overflow-hidden rounded-[14px] p-1.5">
            {hits.map((h) => (
              <li key={h.orgNumber}>
                <button
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => choose(h.orgNumber)}
                  className={cn(
                    "flex w-full items-center justify-between rounded-[10px] px-3 py-2 text-left text-[13px] hover:bg-[rgba(242,139,44,0.14)]"
                  )}
                >
                  <span className="font-semibold text-[var(--ink)]">{h.name ?? h.orgNumber}</span>
                  <span className="tab-num text-[11px] text-[var(--sub)]">{h.orgNumber}</span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
